import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { AxiosResponse } from "axios";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { useBFFProductService } from "@/api/hooks/productService.hook";
import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/components/skeleton/productSliderSkeleton.component";
import { ProductSliderSourceContextProvider } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import { filterVisible } from "@/components/Product/ProductSlider/productSlider.helpers";

type MainProductSliderProps = {
    sku: string;
    serverSideProducts?: Array<Product<ProductFieldSet.ProductTile>>;
};

export const MainProductSlider = ({
    sku,
    children,
    serverSideProducts
}: RequireChildrenFor<MainProductSliderProps>) => {
    const [products, setProducts] = useState(serverSideProducts);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const productService = useBFFProductService();

    const fetchAccessoryProduct = useCallback(
        (sku: string) => {
            return productService
                .get<{}, AxiosResponse<Array<Product<ProductFieldSet.Full>>>>(
                    "/products",
                    {
                        params: {
                            sku: sku,
                            fields: ProductFieldSet.Full
                        }
                    }
                )
                .then(({ data }) => data.at(0));
        },
        [productService.get]
    );

    useEffect(() => {
        if (!sku) return;
        if (isDefined(products)) return;
        if (loading || error) return;

        setLoading(true);

        fetchAccessoryProduct(sku)
            .then((product) => product?.accessoryParents ?? [])
            .then(filterVisible)
            .then(setProducts)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [products, sku, loading, error, fetchAccessoryProduct]);

    if (!sku || error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderSourceContextProvider products={products}>
                {children}
            </ProductSliderSourceContextProvider>
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};
